import React, { useState, useEffect, useMemo } from 'react';
import { FiSearch, FiX, FiRefreshCw, FiStar, FiMessageSquare } from 'react-icons/fi';
import { getTestimonialsByGuilds } from './testimonialService';
import { useAuth } from '../auth/AuthContext';
import { getCoordinatorById } from '../coordinators/coordinatorService';
import { getAllGuilds } from '../guilds/guildService';
import { getAllDoctors } from '../doctors/doctorService';
import '../doctors/DoctorList.css';

export default function CoordinatorTestimonialList() {
    const { currentUser } = useAuth();
    const [testimonials, setTestimonials] = useState([]);
    const [guilds, setGuilds] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRating, setFilterRating] = useState('');

    const filteredTestimonials = useMemo(() => {
        let filtered = [...testimonials];

        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(t =>
                t.feedback?.toLowerCase().includes(searchLower)
            );
        }

        if (filterRating) {
            filtered = filtered.filter(t => t.rating === parseInt(filterRating));
        }

        return filtered;
    }, [testimonials, searchTerm, filterRating]);

    useEffect(() => {
        loadData();
    }, [currentUser]);

    async function loadData() {
        try {
            setLoading(true);
            const coordinator = await getCoordinatorById(currentUser.uid);
            const [testimonialsData, guildsData, doctorsData] = await Promise.all([
                getTestimonialsByGuilds(coordinator?.assignedGuilds || []),
                getAllGuilds(),
                getAllDoctors(),
            ]);
            setTestimonials(testimonialsData);
            setGuilds(guildsData);
            setDoctors(doctorsData);
            setError('');
        } catch (err) {
            setError('Failed to load testimonials');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }


    function getDoctorName(doctorId) {
        const doctor = doctors.find(d => d.id === doctorId);
        return doctor?.fullName || 'Unknown';
    }

    function formatDate(timestamp) {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }

    function renderStars(rating) {
        return (
            <div className="star-rating">
                {[1, 2, 3, 4, 5].map(star => (
                    <FiStar
                        key={star}
                        className={star <= rating ? 'star-filled' : 'star-empty'}
                        style={{ color: star <= rating ? '#FFD700' : '#ccc' }}
                    />
                ))}
            </div>
        );
    }

    if (loading) {
        return (
            <div className="page-container">
                <div className="loading-spinner">
                    <FiRefreshCw className="spin-icon" />
                    <p>Loading testimonials...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="header-left">
                    <h1>Testimonials</h1>
                    <p className="subtitle">View testimonials for doctors in your assigned guilds</p>
                </div>
            </div>

            {error && (
                <div className="error-message">
                    <FiX />
                    {error}
                </div>
            )}

            <div className="filters-section">
                <div className="search-box">
                    <FiSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search feedback..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <select
                    value={filterRating}
                    onChange={(e) => setFilterRating(e.target.value)}
                    className="filter-select"
                >
                    <option value="">All Ratings</option>
                    {[5, 4, 3, 2, 1].map(rating => (
                        <option key={rating} value={rating}>{rating} Stars</option>
                    ))}
                </select>

                {(searchTerm || filterRating) && (
                    <button
                        onClick={() => {
                            setSearchTerm('');
                            setFilterRating('');
                        }}
                        className="btn-clear-filters"
                    >
                        <FiX />
                        <span>Clear Filters</span>
                    </button>
                )}
            </div>

            <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>From</th>
                            <th>To</th>
                            <th>Rating</th>
                            <th>Feedback</th>
                            <th>Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredTestimonials.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="empty-state">
                                    <div className="empty-content">
                                        <FiMessageSquare className="empty-icon" />
                                        <p>
                                            {searchTerm || filterRating
                                                ? 'No testimonials match your filters.'
                                                : 'No testimonials found for your assigned guilds.'}
                                        </p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredTestimonials.map((testimonial) => (
                                <tr key={testimonial.id}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div className="doctor-avatar-circle">
                                                {testimonial.isAnonymous ? '?' : (getDoctorName(testimonial.byDoctorId)[0] || 'D')}
                                            </div>
                                            <span style={{ fontWeight: 500 }}>
                                                {testimonial.isAnonymous
                                                    ? 'Anonymous Doctor'
                                                    : getDoctorName(testimonial.byDoctorId)}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <strong>Dr. {getDoctorName(testimonial.aboutDoctorId)}</strong>
                                            <small style={{ color: '#64748b' }}>Recipient Specialist</small>
                                        </div>
                                    </td>
                                    <td>{renderStars(testimonial.rating)}</td>
                                    <td>
                                        <div className="feedback-preview" title={testimonial.feedback}>
                                            "{testimonial.feedback || 'No feedback provided'}"
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ fontSize: '0.9rem', color: '#444' }}>
                                            {formatDate(testimonial.createdAt)}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
